/* =============================================================================
 * M.M.LaFleur — policy dialog.
 *
 * Opens the shipping, returns and exchanges policy in place instead of
 * navigating away. Anything marked [data-mm-policy] is intercepted; the href
 * stays real so the link still works with JS off, middle-clicked, or opened in
 * a new tab.
 *
 * WHY IT IS A DIALOG
 * ------------------
 * The link sits beside Add to Bag, at the point where someone is deciding
 * whether the terms are acceptable. Navigating away from a configured PDP —
 * colour chosen, size chosen — to read two paragraphs and then find your way
 * back is the kind of small friction that ends sessions. This is the same
 * reasoning, and largely the same machinery, as the size guide.
 *
 * WHERE THE CONTENT COMES FROM
 * ----------------------------
 * Two shapes of URL have to work, because the policy could reasonably be
 * either:
 *
 *   /pages/shipping-returns   a normal page — fetched through the bare view
 *                             (templates/page.mm-bare) so no header, footer or
 *                             theme scripts come with it
 *   /policies/refund-policy   a Shopify policy page — has no custom templates
 *                             at all, so the full document is fetched and the
 *                             policy body extracted from it
 *
 * Both end up as a fragment in the dialog. If neither yields anything usable
 * the click is allowed to proceed as a normal navigation rather than showing
 * an empty box.
 * ========================================================================== */

(function () {
  "use strict";

  /* Platter re-injects the footer's scripts around a Barba container swap. */
  if (window.__mmPolicyBound) return;
  window.__mmPolicyBound = true;

  var CACHE = {};
  var dialog = null;
  var panel = null;
  var titleEl = null;
  var lastFocus = null;

  /* Selectors tried in order when pulling the readable part out of a fetched
     document. The first two are ours, the third is Shopify's own policy
     wrapper, and the rest are progressively more desperate. */
  var CONTENT = [
    "[data-mm-policy-content]",
    ".shopify-policy__body",
    ".mm-scope main",
    "main",
    "[role='main']",
    ".rte"
  ];

  function build() {
    if (dialog) return;

    dialog = document.createElement("div");
    dialog.className = "mm-policy";
    dialog.setAttribute("data-open", "false");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-label", "Shipping and returns");
    dialog.innerHTML =
      '<div class="mm-policy-scrim" data-mm-policy-close></div>' +
      '<div class="mm-policy-panel">' +
        '<div class="mm-policy-head">' +
          '<p class="mm-eyebrow" data-mm-policy-title>Shipping &amp; Returns</p>' +
          '<button type="button" class="mm-policy-close" data-mm-policy-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" ' +
            'stroke-width="1.5" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="mm-policy-body" data-mm-policy-panel></div>' +
      '</div>';

    /* Appended to <body>, not to the section. Platter's header wrapper carries
       a CSS transform, which makes it the containing block for any fixed
       descendant — a panel rendered inside it is trapped in the header's box.
       The cart drawer and search panel are placed this way for the same
       reason. */
    document.body.appendChild(dialog);

    panel = dialog.querySelector("[data-mm-policy-panel]");
    titleEl = dialog.querySelector("[data-mm-policy-title]");

    dialog.addEventListener("click", function (event) {
      if (event.target.closest("[data-mm-policy-close]")) close();
    });
  }

  function open(title) {
    build();
    if (title && titleEl) titleEl.textContent = title;
    dialog.setAttribute("data-open", "true");
    document.body.classList.add("mm-no-scroll");
    var closer = dialog.querySelector(".mm-policy-close");
    if (closer) closer.focus();
  }

  function close() {
    if (!dialog) return;
    dialog.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  /* Shopify page templates are selected with ?view=, which works on /pages/
     but not on /policies/ — those are system pages with no template of their
     own. Asking for the bare view on a policy URL is harmless; it is ignored,
     and the extractor below finds the body either way. */
  function fetchUrl(href) {
    var url = href;
    if (href.indexOf("/pages/") !== -1 && href.indexOf("view=") === -1) {
      url += (href.indexOf("?") === -1 ? "?" : "&") + "view=mm-bare";
    }
    return url;
  }

  function extract(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");

    for (var i = 0; i < CONTENT.length; i += 1) {
      var found = doc.querySelector(CONTENT[i]);
      if (found && found.textContent.trim()) return found.innerHTML;
    }

    /* The bare view renders page content with no wrapper at all, so there may
       be nothing to match — fall back to the body, minus anything scripted. */
    var body = doc.body;
    if (!body) return "";
    Array.prototype.forEach.call(body.querySelectorAll("script, style, noscript"), function (n) {
      n.parentNode.removeChild(n);
    });
    return body.textContent.trim() ? body.innerHTML : "";
  }

  function load(href) {
    if (CACHE[href]) return CACHE[href];
    CACHE[href] = fetch(fetchUrl(href), { headers: { "X-Requested-With": "fetch" } })
      .then(function (r) {
        if (!r.ok) throw new Error("policy fetch failed");
        return r.text();
      })
      .then(extract);
    return CACHE[href];
  }

  /* Bound on window at the capture phase. Barba intercepts link clicks on the
     bubble phase to run its page transition, so a bubble-phase handler here
     would fire after the navigation had already been kicked off.
     stopImmediatePropagation rather than stopPropagation because other
     capture-phase listeners on window are also in play. */
  window.addEventListener("click", function (event) {
    var trigger = event.target.closest && event.target.closest("[data-mm-policy]");
    if (!trigger) return;

    /* Leave the modifier-click and middle-click behaviours alone — someone
       opening this in a new tab has asked for a page, not a dialog. */
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var href = trigger.getAttribute("href");
    if (!href || href === "#") return;

    event.preventDefault();
    event.stopImmediatePropagation();

    lastFocus = trigger;
    var title = trigger.getAttribute("data-mm-policy-title") || "";

    build();
    panel.innerHTML = '<p class="mm-policy-loading">Loading…</p>';
    open(title);

    load(href).then(function (html) {
      if (!html) {
        /* Nothing usable came back. Rather than leave an empty dialog, close
           it and let the link do what it was always going to do. */
        close();
        window.location.href = href;
        return;
      }
      panel.innerHTML = html;
      panel.scrollTop = 0;
    }).catch(function () {
      close();
      window.location.href = href;
    });
  }, true);

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") close();
  });
}());
